import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Grid, Stack, Typography, TextField, Button, Chip, Popover, LinearProgress, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import WorkOutlineRoundedIcon from '@mui/icons-material/WorkOutlineRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded';
import HowToRegRoundedIcon from '@mui/icons-material/HowToRegRounded';
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import http, { apiErrorMessage } from '../../Api/http';
import { GetRecruitmentStats } from '../../Api/Api';
import { field, ghostBtn, LoadAlert, SkeletonCards } from '../uiKit';
import { toApiDate } from '../../utils/apiFields';

// Palette for this page.
const P = {
    purple: '#7547FF', lavender: '#F0EBFF', ink: '#111827', muted: '#64748B', border: '#E5E7F0',
    blue: '#3B82F6', green: '#22C55E', orange: '#F59E0B', red: '#F43F5E', teal: '#14B8A6',
};
const surface = { bgcolor: '#fff', border: `1px solid ${P.border}`, borderRadius: '14px', boxShadow: '0 1px 3px rgba(16,24,40,0.05)' };

const STAGES = [
    { key: 'Applied', color: P.purple, bg: '#F0EBFF', icon: DescriptionOutlinedIcon },
    { key: 'Interviewing', color: P.blue, bg: '#EAF2FF', icon: PeopleAltOutlinedIcon },
    { key: 'On Hold', color: P.orange, bg: '#FEF3E2', icon: AccessTimeOutlinedIcon },
    { key: 'Selected', color: P.teal, bg: '#E4F6F3', icon: CheckCircleOutlineRoundedIcon },
    { key: 'Joined', color: P.green, bg: '#E6F7EF', icon: PersonAddAltOutlinedIcon },
    { key: 'Rejected', color: P.red, bg: '#FDECEF', icon: HighlightOffRoundedIcon },
];
const STATUS_TONE = { Open: ['#15803D', '#ECFDF5'], Closed: ['#64748B', '#F1F5F9'] };

// White section card: icon + title (+ small hint) on the left, an optional "View All" pill on the right.
function SecCard({ icon: Icon, title, hint, action, onAction, children, sx }) {
    return (
        <Box sx={{ ...surface, display: 'flex', flexDirection: 'column', minWidth: 0, ...sx }}>
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 2.4, pt: 2.2, pb: 1.4 }}>
                <Stack direction="row" spacing={1.4} sx={{ alignItems: 'center', minWidth: 0 }}>
                    {Icon && <Icon sx={{ fontSize: 24, color: P.purple, flexShrink: 0 }} />}
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: P.ink, lineHeight: 1.25 }}>{title}</Typography>
                        {hint && <Typography sx={{ fontSize: 12, color: P.muted, mt: 0.2 }}>{hint}</Typography>}
                    </Box>
                </Stack>
                {action && (
                    <Button onClick={onAction} sx={{ textTransform: 'none', fontSize: 12.5, fontWeight: 600, color: P.purple, bgcolor: P.lavender, borderRadius: '8px', px: 1.6, height: 32, flexShrink: 0, '&:hover': { bgcolor: '#E7DFFF' } }}>{action}</Button>
                )}
            </Stack>
            {children}
        </Box>
    );
}

// Headline KPI: big pastel icon tile on the left, label / value / detail on the right, coloured edge.
const KpiTile = ({ label, value, sub, icon: Icon, tone = P.purple, bg = P.lavender }) => (
    <Box sx={{ ...surface, p: 2.2, height: '100%', position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'flex-start', gap: 2, '&::before': { content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, bgcolor: tone } }}>
        <Box sx={{ width: 60, height: 60, borderRadius: '16px', bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon sx={{ color: tone, fontSize: 30 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: 0.6 }}>{label}</Typography>
            <Typography sx={{ fontSize: 32, fontWeight: 800, color: P.ink, lineHeight: 1.15, mt: 0.3 }}>{value}</Typography>
            {sub && <Typography sx={{ fontSize: 12.5, color: P.muted, mt: 0.4 }}>{sub}</Typography>}
        </Box>
    </Box>
);

// Compact tile for the Interviews card.
const MiniTile = ({ label, value, sub, icon: Icon, tone, bg, sx }) => (
    <Box sx={{ p: 1.6, borderRadius: '12px', bgcolor: bg, display: 'flex', alignItems: 'center', gap: 1.4, minWidth: 0, ...sx }}>
        <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon sx={{ color: tone, fontSize: 22 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 12, color: P.muted, fontWeight: 600 }} noWrap>{label}</Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: P.ink, lineHeight: 1.2 }}>{value}</Typography>
            {sub && <Typography sx={{ fontSize: 11.5, color: P.muted }} noWrap>{sub}</Typography>}
        </Box>
    </Box>
);

// One pipeline stage: flat pastel card with a soft icon, the count, its share of all candidates and a slim bar.
// Rejected only turns red once it actually has candidates; at zero it stays neutral.
function StageCard({ stage, value, total }) {
    const quiet = stage.key === 'Rejected' && value === 0;
    const color = quiet ? '#94A3B8' : stage.color;
    const bg = quiet ? '#F8FAFC' : stage.bg;
    const pct = total ? Math.round((value / total) * 100) : 0;
    const Icon = stage.icon;
    return (
        <Box sx={{ p: 1.8, borderRadius: '12px', bgcolor: bg, border: `1px solid ${color}26`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', minWidth: 0 }}>
            <Box sx={{ width: 42, height: 42, borderRadius: '50%', bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 3px rgba(16,24,40,0.06)' }}>
                <Icon sx={{ fontSize: 22, color }} />
            </Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color, mt: 1.2 }} noWrap>{stage.key}</Typography>
            <Typography sx={{ fontSize: 26, fontWeight: 800, color: P.ink, lineHeight: 1.15, mt: 0.4 }}>{value}</Typography>
            <Typography sx={{ fontSize: 12, color: quiet ? '#94A3B8' : color, fontWeight: 600, mt: 0.2 }}>{pct}%</Typography>
            <Box sx={{ width: '100%', height: 5, borderRadius: 3, bgcolor: '#fff', mt: 1.4, overflow: 'hidden' }}>
                <Box sx={{ width: `${pct}%`, height: '100%', borderRadius: 3, bgcolor: color, transition: 'width .4s ease' }} />
            </Box>
        </Box>
    );
}

// Ring chart: segment sizes follow the four interview counts, the centre shows the overall total.
function Donut({ segments, total }) {
    const size = 150; const stroke = 16; const r = (size - stroke) / 2; const circ = 2 * Math.PI * r;
    const sum = segments.reduce((a, s) => a + s.value, 0);
    let offset = 0;
    return (
        <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)', display: 'block' }}>
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#EEF1F6" strokeWidth={stroke} />
                {sum > 0 && segments.map((s) => {
                    if (!s.value) return null;
                    const len = (s.value / sum) * circ;
                    const el = <circle key={s.label} cx={size / 2} cy={size / 2} r={r} fill="none" stroke={s.color} strokeWidth={stroke} strokeDasharray={`${len} ${circ - len}`} strokeDashoffset={-offset} />;
                    offset += len;
                    return el;
                })}
            </svg>
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                <Typography sx={{ fontSize: 28, fontWeight: 800, color: P.ink, lineHeight: 1 }}>{total}</Typography>
                <Typography sx={{ fontSize: 11.5, color: P.muted, mt: 0.4, lineHeight: 1.2 }}>Total<br />Interviews</Typography>
            </Box>
        </Box>
    );
}

// Horizontal bars scaled to the largest value, with each row's share of the total.
function Bars({ rows }) {
    const max = Math.max(1, ...rows.map((r) => r.value));
    const total = rows.reduce((a, r) => a + r.value, 0) || 1;
    return (
        <Stack spacing={1.6}>
            {rows.map((r) => {
                // The leading source is purple; the rest use a muted tone.
                const color = r.color || (r.value === max && r.value > 0 ? P.purple : '#B8C0D0');
                return (
                    <Box key={r.label}>
                        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 0.6 }}>
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: color }} />
                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{r.label}</Typography>
                            </Stack>
                            <Typography sx={{ fontSize: 13, fontWeight: 800, color: P.ink }}>
                                {r.value}
                                <Box component="span" sx={{ ml: 1, fontSize: 12, fontWeight: 500, color: P.muted }}>{Math.round((r.value / total) * 100)}%</Box>
                            </Typography>
                        </Stack>
                        <Box sx={{ height: 8, borderRadius: 5, bgcolor: '#EEF1F6', overflow: 'hidden' }}>
                            <Box sx={{ height: '100%', borderRadius: 5, width: `${(r.value / max) * 100}%`, bgcolor: color, transition: 'width .4s ease' }} />
                        </Box>
                    </Box>
                );
            })}
        </Stack>
    );
}

export default function OverviewTab({ setActions }) {
    const navigate = useNavigate();
    const [rangeAnchor, setRangeAnchor] = useState(null);
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const { data: body } = await http.get(GetRecruitmentStats, {
                params: { fromDate: from ? toApiDate(from) : undefined, toDate: to ? toApiDate(to) : undefined },
            });
            if (body?.error) throw new Error(body.message || 'Could not load recruitment stats.');
            setData(body?.data ?? null);
        } catch (err) {
            setError(err?.response || err?.request ? apiErrorMessage(err, 'Could not load recruitment stats.') : err.message);
        } finally {
            setLoading(false);
        }
    }, [from, to]);
    useEffect(() => { load(); }, [load]);

    const o = data?.overall;
    const v = o?.vacancies || {};
    const c = o?.candidates || {};
    const iv = o?.interviews || {};
    const sources = Object.entries(c.bySource || {}).map(([label, value]) => ({ label, value: Number(value) || 0 }));

    const ivTotal = iv.total ?? 0;
    const ivSegments = [
        { label: 'Scheduled', value: Number(iv.scheduled) || 0, color: P.purple },
        { label: 'Conducted', value: Number(iv.conducted) || 0, color: P.blue },
        { label: 'Completed', value: Number(iv.completed) || 0, color: P.green },
        { label: 'Upcoming', value: Number(iv.upcoming) || 0, color: P.orange },
    ];

    const dateField = { ...field, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], bgcolor: '#fff' } };

    // Publish the date-range button to the breadcrumb row (RecruitmentPage places it beside the page buttons).
    const fmt = (d) => (d ? d.split('-').reverse().join('-') : '…');
    const rangeLabel = from || to ? `${fmt(from)} – ${fmt(to)}` : 'Applied dates';
    useEffect(() => {
        if (!setActions) return undefined;
        setActions(
            <Box
                role="button" aria-label="Choose applied date range"
                onClick={(e) => setRangeAnchor(e.currentTarget)}
                sx={{
                    height: 42, px: 1.6, borderRadius: '7px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1,
                    bgcolor: from || to ? '#F3F0FE' : '#fff', color: from || to ? '#7547FF' : '#334155',
                    border: `1px solid ${from || to ? '#C9BEFB' : '#E5E7EB'}`, fontSize: 13, fontWeight: 700,
                    '&:hover': { bgcolor: '#F3F0FE', borderColor: '#C9BEFB', color: '#7547FF' },
                }}
            >
                <CalendarMonthRoundedIcon sx={{ fontSize: 19, color: '#7547FF' }} />
                {rangeLabel}
            </Box>,
        );
        return () => setActions(null);
    }, [setActions, rangeLabel, from, to]);

    return (
        <Box sx={{ p: 2 }}>
            {/* The applied-date range lives in the breadcrumb row (set below); its panel opens from there. */}
            <Popover
                open={Boolean(rangeAnchor)}
                anchorEl={rangeAnchor}
                onClose={() => setRangeAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { p: 2, mt: 1, width: 300, borderRadius: '12px' } } }}
            >
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: P.ink, mb: 0.3 }}>Applied dates</Typography>
                <Typography sx={{ fontSize: 12, color: P.muted, mb: 1.6 }}>Limit candidates by the date they applied.</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.4 }}>
                    <TextField label="Applied from" type="date" size="small" fullWidth value={from} onChange={(e) => setFrom(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} sx={dateField} />
                    <TextField label="Applied to" type="date" size="small" fullWidth value={to} onChange={(e) => setTo(e.target.value)} slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: from || undefined } }} sx={dateField} />
                    {(from || to) && <Button onClick={() => { setFrom(''); setTo(''); }} sx={{ ...ghostBtn, height: 36 }}>Clear</Button>}
                </Box>
            </Popover>

            {loading ? <SkeletonCards count={4} /> : error ? <LoadAlert>{error}</LoadAlert> : !o ? <LoadAlert>There is nothing to show here right now.</LoadAlert> : (
                <>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid size={{ xs: 12, sm: 6, lg: 3 }}><KpiTile icon={WorkOutlineRoundedIcon} label="Vacancies" value={v.total ?? 0} sub={`${v.open ?? 0} open · ${v.closed ?? 0} closed · ${v.published ?? 0} published`} /></Grid>
                        <Grid size={{ xs: 12, sm: 6, lg: 3 }}><KpiTile icon={GroupAddRoundedIcon} tone={P.blue} bg="#EAF2FF" label="Openings" value={v.totalOpenings ?? 0} sub={`${v.filled ?? 0} filled · ${v.stillOpen ?? 0} still open`} /></Grid>
                        <Grid size={{ xs: 12, sm: 6, lg: 3 }}><KpiTile icon={PeopleAltRoundedIcon} tone={P.green} bg="#E6F7EF" label="Candidates" value={c.total ?? 0} sub={`${c.inPipeline ?? 0} in pipeline · ${c.joined ?? 0} joined · ${c.rejected ?? 0} rejected`} /></Grid>
                        <Grid size={{ xs: 12, sm: 6, lg: 3 }}><KpiTile icon={TrendingUpRoundedIcon} tone={P.orange} bg="#FEF3E2" label="Conversion" value={`${c.conversionRate ?? 0}%`} sub={`Avg. days to join: ${c.averageDaysToJoin ?? '—'}`} /></Grid>
                    </Grid>

                    {/* Candidates by stage (8/12) + Interviews (4/12), then By role (8/12) + Candidates by source (4/12). */}
                    <Box sx={{
                        display: 'grid', gap: 2, alignItems: 'stretch',
                        gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(12, minmax(0, 1fr))' },
                        gridTemplateAreas: {
                            xs: '"stage" "ivs" "src" "role"',
                            lg: '"stage stage stage stage stage stage stage stage stage stage stage stage" "ivs ivs ivs ivs ivs ivs src src src src src src" "role role role role role role role role role role role role"',
                        },
                    }}>
                        <Box sx={{ gridArea: 'stage', minWidth: 0, display: 'flex' }}>
                            <SecCard icon={BarChartRoundedIcon} title="Candidates by stage" hint="Total candidates in each stage of the hiring process" sx={{ flex: 1 }}>
                                {(() => {
                                    const counts = STAGES.map((s) => Number(c.byStage?.[s.key]) || 0);
                                    const total = counts.reduce((a, x) => a + x, 0);
                                    return (
                                        <Box sx={{ p: 2.4, pt: 1.2, display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', md: 'repeat(6, minmax(0, 1fr))' } }}>
                                            {STAGES.map((s, i) => <StageCard key={s.key} stage={s} value={counts[i]} total={total} />)}
                                        </Box>
                                    );
                                })()}
                            </SecCard>
                        </Box>

                        <Box sx={{ gridArea: 'ivs', minWidth: 0, display: 'flex' }}>
                            <SecCard icon={EventRoundedIcon} title="Interviews & Status" sx={{ flex: 1 }}>
                                <Box sx={{ p: 2.4, pt: 1.2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Stack direction="row" spacing={2.4} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1.5 }}>
                                        <Donut segments={ivSegments} total={ivTotal} />
                                        <Box sx={{ flex: 1, minWidth: 190, border: `1px solid ${P.border}`, borderRadius: '12px', px: 1.6, py: 1.2 }}>
                                            {ivSegments.map((s) => (
                                                <Stack key={s.label} direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', py: 0.7 }}>
                                                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                                        <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: s.color }} />
                                                        <Typography sx={{ fontSize: 13, color: '#334155' }}>{s.label}</Typography>
                                                    </Stack>
                                                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: P.ink }}>
                                                        {s.value}
                                                        <Box component="span" sx={{ ml: 1.2, fontSize: 12, fontWeight: 500, color: P.muted }}>{ivTotal ? Math.round((s.value / ivTotal) * 100) : 0}%</Box>
                                                    </Typography>
                                                </Stack>
                                            ))}
                                        </Box>
                                    </Stack>
                                    <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: '1fr 1fr' }}>
                                        <MiniTile icon={EventBusyRoundedIcon} tone={P.red} bg="#FDECEF" label="Cancelled / No-show" value={`${iv.cancelled ?? 0} / ${iv.noShow ?? 0}`} />
                                        <MiniTile icon={EventAvailableRoundedIcon} tone={P.blue} bg="#EAF2FF" label="Upcoming" value={iv.upcoming ?? 0} sub={`${iv.today ?? 0} today`} />
                                        <MiniTile icon={HowToRegRoundedIcon} tone={P.teal} bg="#E4F6F3" label="Outcomes" value={`${iv.outcomes?.Selected ?? 0} / ${iv.outcomes?.Rejected ?? 0} / ${iv.outcomes?.['On Hold'] ?? 0}`} sub="Selected / Rejected / On Hold" sx={{ gridColumn: '1 / -1' }} />
                                    </Box>
                                </Box>
                            </SecCard>
                        </Box>

                        <Box sx={{ gridArea: 'src', minWidth: 0, display: 'flex' }}>
                            <SecCard sx={{ flex: 1 }} icon={PublicRoundedIcon} title="Candidates by source" hint={`${c.appliedOnCareersPage ?? 0} via careers page · ${c.addedByHr ?? 0} added by HR`} action="View All" onAction={() => navigate('/dashboard/recruitment?tab=candidates')}>
                                <Box sx={{ p: 2.4, pt: 1.2 }}>{sources.length ? <Bars rows={sources} /> : <Typography sx={{ fontSize: 12.5, color: '#98A0AE' }}>No candidates yet.</Typography>}</Box>
                            </SecCard>
                        </Box>

                        <Box sx={{ gridArea: 'role', minWidth: 0, alignSelf: 'start' }}>
                            <SecCard icon={GroupsRoundedIcon} title="By role" action="View All" onAction={() => navigate('/dashboard/recruitment?tab=vacancies')}>
                                <Box sx={{ overflowX: 'auto', px: 1.4, pb: 1.4 }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ '& th': { bgcolor: '#F4F2FF', borderBottom: 'none' }, '& th:first-of-type': { borderRadius: '8px 0 0 8px' }, '& th:last-of-type': { borderRadius: '0 8px 8px 0' } }}>
                                                {['Role', 'Status', 'Filled', 'Days open', 'Applicants', 'In pipeline', 'Joined', 'Rejected', 'Conv.', 'Interviews'].map((h) => (
                                                    <TableCell key={h} sx={{ fontSize: 11, fontWeight: 700, color: P.muted, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap', py: 1.2 }}>{h}</TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(data.vacancies || []).map((r) => {
                                                const [fg, bg] = STATUS_TONE[r.status] || ['#B45309', '#FFF7ED'];
                                                const pct = r.openings ? Math.min(100, (r.filled / r.openings) * 100) : 0;
                                                return (
                                                    <TableRow key={r.id} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                                                        <TableCell sx={{ fontWeight: 700, color: P.ink, whiteSpace: 'nowrap', py: 1.4 }}>{r.roleTitle}</TableCell>
                                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                            <Chip label={r.status} size="small" sx={{ bgcolor: bg, color: fg, fontWeight: 700, fontSize: 11.5 }} />
                                                            {r.isPublished && <Chip label="Live" size="small" sx={{ ml: 0.6, bgcolor: P.lavender, color: P.purple, fontWeight: 700, fontSize: 11.5 }} />}
                                                        </TableCell>
                                                        <TableCell sx={{ minWidth: 120 }}>
                                                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>{r.filled} / {r.openings}</Typography>
                                                            <LinearProgress variant="determinate" value={pct} sx={{ height: 5, borderRadius: 3, mt: 0.4, bgcolor: '#EEF1F6', '& .MuiLinearProgress-bar': { bgcolor: P.green, borderRadius: 3 } }} />
                                                        </TableCell>
                                                        <TableCell>{r.daysOpen ?? '—'}</TableCell>
                                                        <TableCell>{r.applicants}</TableCell>
                                                        <TableCell>{r.inPipeline}</TableCell>
                                                        <TableCell sx={{ color: '#15803D', fontWeight: 700 }}>{r.joined}</TableCell>
                                                        <TableCell sx={{ color: '#E11D48', fontWeight: 700 }}>{r.rejected}</TableCell>
                                                        <TableCell>{r.conversionRate ?? 0}%</TableCell>
                                                        <TableCell>{r.interviews?.total ?? 0}</TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                            {!(data.vacancies || []).length && (
                                                <TableRow><TableCell colSpan={10} sx={{ textAlign: 'center', color: '#98A0AE', py: 4 }}>No vacancies yet.</TableCell></TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </Box>
                            </SecCard>
                        </Box>
                    </Box>
                </>
            )}
        </Box>
    );
}
